import './lesson-sidebar.less';
import { useRef, useEffect, useState } from 'react';
import { HelpCircle, MessageCircle, ArrowLeft, ChevronDown, ChevronRight, CheckCircle, X, Check } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import type { CollegeChapterItem, CollegeStepItem } from '@zrt-ai-office/types/course';

interface LessonSidebarProps {
	courseTitle: string;
	chapters: CollegeChapterItem[];
	steps: CollegeStepItem[];
	activeChapterId: number | null;
	activeStepId: number | null;
	onSelectChapter: (id: number) => void;
	onSelectStep: (stepId: number) => void;
	onBackToCourse?: () => void;
	onContinueLearning?: () => void;
	allCompleted?: boolean;
	mobileOpen?: boolean;
	onMobileClose?: () => void;
}

export function LessonSidebar({
	courseTitle,
	chapters,
	steps,
	activeChapterId,
	activeStepId,
	onSelectChapter,
	onSelectStep,
	onBackToCourse,
	onContinueLearning,
	allCompleted,
	mobileOpen,
	onMobileClose,
}: LessonSidebarProps) {
	const toast = useToast();
	const activeRef = useRef<HTMLLIElement>(null);
	const [expandedChapterId, setExpandedChapterId] = useState<number | null>(activeChapterId);

	useEffect(() => {
		if (activeRef.current) {
			activeRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
		}
	}, [activeChapterId]);

	useEffect(() => {
		setExpandedChapterId(activeChapterId);
	}, [activeChapterId]);

	const firstUnlearned = chapters.find((ch) => !ch.learned);

	const handleSelectChapter = (id: number) => {
		onSelectChapter(id);
		setExpandedChapterId(expandedChapterId === id ? null : id);
		if (onMobileClose) onMobileClose();
	};

	const handleSelectStep = (stepId: number) => {
		onSelectStep(stepId);
		if (onMobileClose) onMobileClose();
	};

	const sidebarContent = (
		<>
			<div className="lesson-sidebar__header">
				<div className="lesson-sidebar__title-row">
					{onBackToCourse && (
						<button className="lesson-sidebar__back-btn" onClick={onBackToCourse} type="button">
							<ArrowLeft className="h-4 w-4" />
						</button>
					)}
					<h2 className="lesson-sidebar__course-title">{courseTitle}</h2>
					{onMobileClose && (
						<button className="lesson-sidebar__close-btn" onClick={onMobileClose} type="button">
							<X className="h-4 w-4" />
						</button>
					)}
				</div>

				{allCompleted && (
					<div className="lesson-sidebar__completed-badge">
						<CheckCircle className="lesson-sidebar__completed-icon" />
						<span>已全部完成</span>
					</div>
				)}
			</div>

			<div className="lesson-sidebar__separator" />

			<nav className="lesson-sidebar__nav">
				<ul className="lesson-sidebar__chapter-list">
					{chapters.map((chapter, index) => {
						const isActive = chapter.id === activeChapterId;
						const isLearned = chapter.learned;
						const isExpanded = expandedChapterId === chapter.id;
						const chapterSteps = isExpanded && isActive ? steps : [];

						return (
							<li key={chapter.id} ref={isActive ? activeRef : undefined}>
								<button
									className={`lesson-sidebar__chapter-btn ${isActive ? 'lesson-sidebar__chapter-btn--active' : ''}`}
									onClick={() => handleSelectChapter(chapter.id)}
									type="button"
								>
									<span className="lesson-sidebar__chapter-indicator">
										{isLearned && (
											<Check className="lesson-sidebar__chapter-check" />
										)}
										<span
											className={`lesson-sidebar__chapter-index ${isLearned ? 'lesson-sidebar__chapter-index--completed' : isActive ? 'lesson-sidebar__chapter-index--active' : ''}`}
										>
											{index + 1}
										</span>
									</span>
									<div className="lesson-sidebar__chapter-info">
										<p
											className={`lesson-sidebar__chapter-title ${isActive ? 'lesson-sidebar__chapter-title--active' : ''} ${isLearned ? 'lesson-sidebar__chapter-title--completed' : ''}`}
										>
											{chapter.title}
										</p>
										{chapter.intro && <p className="lesson-sidebar__chapter-intro">{chapter.intro}</p>}
									</div>
									{isActive && (
										<span className="lesson-sidebar__expand-icon">{isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}</span>
									)}
								</button>

								{chapterSteps.length > 0 && isExpanded && (
									<ul className="lesson-sidebar__step-list">
										{chapterSteps.map((step) => {
											const isStepActive = step.id === activeStepId;
											return (
												<li key={step.id}>
													<button
														className={`lesson-sidebar__step-btn ${isStepActive ? 'lesson-sidebar__step-btn--active' : ''}`}
														onClick={() => handleSelectStep(step.id)}
														type="button"
													>
														<span className="lesson-sidebar__step-dot" />
														<span className="lesson-sidebar__step-title">{step.title}</span>
													</button>
												</li>
											);
										})}
									</ul>
								)}
							</li>
						);
					})}
				</ul>
			</nav>

			<div className="lesson-sidebar__footer">
				<button className="lesson-sidebar__footer-btn" onClick={() => toast.info('帮助中心即将上线')} type="button">
					<HelpCircle className="h-3.5 w-3.5" />
					帮助中心
				</button>
				<button className="lesson-sidebar__footer-btn" onClick={() => toast.info('反馈功能即将上线')} type="button">
					<MessageCircle className="h-3.5 w-3.5" />
					反馈建议
				</button>
			</div>
		</>
	);

	return (
		<>
			<aside className="lesson-sidebar">{sidebarContent}</aside>

			{mobileOpen && (
				<div className="lesson-sidebar__overlay" onClick={onMobileClose}>
					<aside className="lesson-sidebar lesson-sidebar--mobile" onClick={(e) => e.stopPropagation()}>
						{sidebarContent}
					</aside>
				</div>
			)}
		</>
	);
}
